/**
 * WorkflowV2 — entry component for the redesigned TARA workflow tab
 * (TypeScript, post Story 3.Y).
 *
 * Thin dispatcher. Owns:
 *   - the v2 reducer + the `actions` creators bound to its dispatch
 *   - the local `threadId` override (after Start Analysis returns a
 *     fresh thread_id)
 *   - the workflow-wide polling that hydrates the reducer
 *   - per-step orchestration hooks (one composed hook per step)
 *
 * The orchestrator no longer contains the per-step async lifecycle —
 * each step's logic lives in its dedicated hook
 * (`step-hooks/useStepN<Name>.ts`). Stories 4-11 add one render
 * branch + one hook each. The total length here stays roughly
 * constant as steps are added.
 */
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import WorkflowV2AppShell from './components/WorkflowV2AppShell';
import StepShell from './step-components/StepShell';
import StepPlaceholder from './step-components/StepPlaceholder';
import Step0ItemInitiation from './step-components/Step0ItemInitiation';
import Step1ItemDefinition from './step-components/Step1ItemDefinition';
import Step2AssetIdentification from './step-components/Step2AssetIdentification';
import Step3DamageScenarios from './step-components/Step3DamageScenarios';
import Step4ThreatIdentification from './step-components/Step4ThreatIdentification';
import Step5AttackPathFeasibility from './step-components/Step5AttackPathFeasibility';
import Step6RiskAssessment from './step-components/Step6RiskAssessment';
import Step7CybersecurityGoals from './step-components/Step7CybersecurityGoals';
import Step8CybersecurityConcept from './step-components/Step8CybersecurityConcept';
import { workflowReducer, initialState, isStepLocked } from './state/workflowV2Reducer';
import { createWorkflowActions } from './state/workflowV2Actions';
import { findStep, stepToAgent } from './state/workflowV2StepCatalog';
import { useWorkflowStatusPolling } from './hooks/useWorkflowStatusPolling';
import { useChatPersistence } from './hooks/useChatPersistence';
import { useMountedRef } from '../../hooks/useMountedRef';
import { useStep0ItemInitiation } from './step-hooks/useStep0ItemInitiation';
import { useStep1ItemDefinition } from './step-hooks/useStep1ItemDefinition';
import { useStep2AssetIdentification } from './step-hooks/useStep2AssetIdentification';
import { useStep3DamageScenarios } from './step-hooks/useStep3DamageScenarios';
import { useStep4ThreatIdentification } from './step-hooks/useStep4ThreatIdentification';
import { useStep5AttackPaths } from './step-hooks/useStep5AttackPaths';
import { useStep6RiskAssessment } from './step-hooks/useStep6RiskAssessment';
import { useStep7GoalsClaims } from './step-hooks/useStep7GoalsClaims';
import { useStep8Concept } from './step-hooks/useStep8Concept';
import { useApproveFlow } from './hooks/useApproveFlow';
import { useResumeFlow } from './hooks/useResumeFlow';
import { notify } from '../../services/notificationBus';
import type {
  BackendWorkflowProgress,
  WorkflowActions,
  WorkflowV2State,
} from './state/workflowV2Types';

const UPLOAD_STEP_ID = 0;

export interface WorkflowV2Props {
  projectId: string;
  threadId?: string | null;
  tenantId?: string | null;
  /** Session-level `analysis_mode` — `'hierarchical'` for parent TARAs
   *  aggregating child TARAs, `'individual'` (or undefined) otherwise.
   *  Drives Step 2's two-tab review and (in later stories) the
   *  hierarchical review surfaces for Steps 3-8. */
  analysisMode?: string | null;
  /** Primary uploaded document id for this session — powers the
   *  "View uploaded document" action on the item-definition step. */
  fileId?: string | null;
}

export default function WorkflowV2({
  projectId,
  threadId: threadIdProp,
  tenantId,
  analysisMode,
  fileId,
}: WorkflowV2Props) {
  const [state, dispatch] = useReducer(workflowReducer, initialState) as [
    WorkflowV2State,
    React.Dispatch<any>,
  ];
  const actions: WorkflowActions = useMemo(
    () => createWorkflowActions(dispatch),
    [dispatch],
  );
  const locked = isStepLocked(state, state.selectedStepId);

  /* ---------- URL ↔ reducer sync (?step=N) ---------- */

  const [searchParams, setSearchParams] = useSearchParams();

  // One-shot seed: on first mount, jump to the step encoded in the URL
  // (if any). After that, the state-→-URL effect below owns sync.
  // Also reads `?mode=` (Story 15.C.3) to seed Step 0's wizard mode
  // so refresh on the chooser preserves the user's pick.
  const urlSeededRef = useRef(false);
  useEffect(() => {
    if (urlSeededRef.current) return;
    urlSeededRef.current = true;
    const stepParam = searchParams.get('step');
    if (stepParam != null) {
      const stepId = parseInt(stepParam, 10);
      // Story H9 — guard against URL-injected garbage. `?step=99`
      // or `?step=-1` previously dispatched to a non-existent slot;
      // catalog check ensures only valid step IDs reach the reducer.
      if (
        Number.isFinite(stepId) &&
        findStep(stepId) &&
        stepId !== state.selectedStepId
      ) {
        actions.selectStep(stepId);
      }
    }
    const modeParam = searchParams.get('mode');
    if (modeParam === 'automatic' || modeParam === 'manual' || modeParam === 'diagram') {
      actions.setStepMode(UPLOAD_STEP_ID, modeParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reducer's selectedStepId → URL. `replace: true` keeps every rail
  // click off the browser history stack (no per-step history clutter).
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const want = String(state.selectedStepId);
        if (next.get('step') === want) return prev;
        next.set('step', want);
        // Different step → clear inner-tab section so we don't carry
        // a stale `?section=` from a previous step into the new one.
        next.delete('section');
        return next;
      },
      { replace: true },
    );
  }, [state.selectedStepId, setSearchParams]);

  // Step-0 selectedMode → URL `?mode=` (Story 15.C.3). Survives
  // refresh — bookmarked URL preserves the user's wizard pick.
  // Cleared when mode is null (back to the chooser).
  useEffect(() => {
    const mode = state.steps[UPLOAD_STEP_ID]?.selectedMode ?? null;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (mode === null) {
          if (!next.has('mode')) return prev;
          next.delete('mode');
        } else {
          if (next.get('mode') === mode) return prev;
          next.set('mode', mode);
        }
        return next;
      },
      { replace: true },
    );
  }, [state.steps, setSearchParams]);

  // Local threadId override — set after Start Analysis returns a
  // fresh thread_id (new project flow). Preserved across this mount;
  // ProjectWorkspace re-fetches the project on next visit.
  const [localThreadId, setLocalThreadId] = useState<string | null>(null);
  const threadId: string | null = localThreadId || threadIdProp || null;

  const sessionId = projectId;
  const stepId = state.selectedStepId;

  /* ---------- Workflow-wide status polling (hydrates reducer) ---------- */

  const handleSnapshot = useCallback(
    (workflowProgress: BackendWorkflowProgress | null | undefined) => {
      if (workflowProgress) actions.hydrate(workflowProgress);
    },
    [actions],
  );

  const { refetch: refetchStatus } = useWorkflowStatusPolling({
    threadId,
    onSnapshot: handleSnapshot,
  });

  /* ---------- Step 0 seeding for existing projects ---------- */

  // If the project already has a thread_id, the upload step is
  // implicitly done — mark it approved. Step selection is
  // deferred to the unified auto-advance effect below so we never
  // flash Step 1 content before settling on the actually-active
  // step (Option B per the URL-less-landing UX review).
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (threadIdProp) {
      actions.setStepStatus(UPLOAD_STEP_ID, 'approved');
      seededRef.current = true;
    }
  }, [threadIdProp, actions]);

  /* ---------- URL-less landing: wait-for-hydrate, pick once -----------
   *
   * Single source of step selection on mount when `?step=` isn't in
   * the URL. Waits for the first polling hydrate to populate real
   * step statuses, then dispatches one `selectStep` to the right
   * destination:
   *
   *   - Mid-flow (any step ∈ draft/revising/generating) → that step
   *   - Else (all empty or all approved) → Step 1
   *
   * Without this gate, a mid-flow project flashed Step 1 content
   * for ~3s before snapping to (e.g.) Step 4, because the legacy
   * pattern was "selectStep(1) on mount, then auto-advance on first
   * hydrate." The unified effect collapses both into one dispatch
   * after the data is known.
   *
   * Fallback: if polling stalls for 5s and we still haven't seen a
   * hydrate, force-select Step 1 so the user isn't stranded on the
   * "Setup approved" Step-0 placeholder indefinitely.
   *
   * URL `?step=` wins unconditionally — both branches early-return.
   */
  const autoAdvancedRef = useRef(false);
  useEffect(() => {
    if (autoAdvancedRef.current) return;
    if (searchParams.get('step')) return; // explicit URL wins
    if (!threadId) return;                 // brand-new project — stay on Step 0
    // Only consider advancing once we've seen at least one hydrate
    // tick — otherwise every step is `empty` (initial reducer state)
    // and the lookup returns null.
    const anyHydrated = Object.values(state.steps).some(
      (s) => s.status && s.status !== 'empty',
    );
    if (!anyHydrated) return;

    const activeStatuses = new Set<string>(['draft', 'revising', 'generating']);
    const candidate = Object.entries(state.steps).find(([id, slot]) => {
      const stepIdNum = Number(id);
      return stepIdNum >= 1 && stepIdNum <= 8 && activeStatuses.has(slot.status);
    });
    autoAdvancedRef.current = true;
    const targetId = candidate ? Number(candidate[0]) : 1;
    if (targetId !== state.selectedStepId) {
      actions.selectStep(targetId);
    }
  }, [state.steps, state.selectedStepId, threadId, searchParams, actions]);

  // Fallback: if polling hasn't produced a hydrate within 5s (slow
  // backend / network blip), default to Step 1 so we don't leave the
  // user staring at the Step 0 "Setup approved" placeholder forever.
  useEffect(() => {
    if (autoAdvancedRef.current) return;
    if (searchParams.get('step')) return;
    if (!threadId) return;
    const t = window.setTimeout(() => {
      if (autoAdvancedRef.current) return;
      autoAdvancedRef.current = true;
      if (state.selectedStepId !== 1) actions.selectStep(1);
    }, 5000);
    return () => window.clearTimeout(t);
    // Run once per (threadId), not per state.steps update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, searchParams]);

  /* ---------- Workflow-completed milestone toast (Story 15.C.1) ----------
   *
   * Fires once on the transition `workflowCompleted: false → true` —
   * the moment Step 8 hits `approved` AND every prior agent-backed
   * step is also approved. Ref-guarded so re-opening an already-
   * completed project doesn't fire the toast on every mount.
   */
  const completedToastFiredRef = useRef(false);
  useEffect(() => {
    if (!state.workflowCompleted) return;
    if (completedToastFiredRef.current) return;
    completedToastFiredRef.current = true;
    notify({
      type: 'success',
      title: 'TARA Approved',
      message: 'All ISO/SAE 21434 clauses complete. Open the Reports tab to export, or switch to Residual TARA to continue.',
      duration: 8000,
    });
  }, [state.workflowCompleted]);

  /* ---------- Per-step composition hooks ---------- */

  const step0Props = useStep0ItemInitiation({
    sessionId,
    tenantId,
    actions,
    stepState: state.steps[UPLOAD_STEP_ID],
    onThreadIdResolved: setLocalThreadId,
  });

  const step1Props = useStep1ItemDefinition({
    threadId,
    stepState: state.steps[1],
    workflowCompleted: state.workflowCompleted,
  });

  const step2Props = useStep2AssetIdentification({
    threadId,
    stepState: state.steps[2],
    workflowCompleted: state.workflowCompleted,
    analysisMode,
    state,
    actions,
  });

  const step3Props = useStep3DamageScenarios({
    threadId,
    stepState: state.steps[3],
    workflowCompleted: state.workflowCompleted,
    analysisMode,
    state,
    actions,
  });

  const step4Props = useStep4ThreatIdentification({
    threadId,
    stepState: state.steps[4],
    workflowCompleted: state.workflowCompleted,
    analysisMode,
    state,
    actions,
  });

  const step5Props = useStep5AttackPaths({
    threadId,
    stepState: state.steps[5],
    workflowCompleted: state.workflowCompleted,
    analysisMode,
    state,
    actions,
  });

  const step6Props = useStep6RiskAssessment({
    threadId,
    stepState: state.steps[6],
    workflowCompleted: state.workflowCompleted,
    analysisMode,
    state,
    actions,
  });

  const step7Props = useStep7GoalsClaims({
    threadId,
    stepState: state.steps[7],
    workflowCompleted: state.workflowCompleted,
    analysisMode,
    state,
    actions,
  });

  const step8Props = useStep8Concept({
    threadId,
    stepState: state.steps[8],
    workflowCompleted: state.workflowCompleted,
    analysisMode,
    state,
    actions,
  });

  const approve = useApproveFlow({
    stepId,
    threadId,
    sessionId,
    tenantId,
    refetchStatus,
    actions,
    workflowCompleted: state.workflowCompleted,
  });

  // Recovery for a FAILED step: re-enqueues the run from the last
  // checkpoint (only the crashed agent re-runs). Rendered by the
  // ActionPanel in place of the Approve CTA when status === 'failed'.
  const resume = useResumeFlow({
    stepId,
    threadId,
    sessionId,
    tenantId,
    refetchStatus,
    actions,
  });

  /* ---------- Reviewer chat persistence ----------
   *
   * Wires the per-step chat thread to the v2 chat endpoints. Hydrates
   * on first open of each (thread, agent) pair, exposes optimistic
   * `sendUser` / `recvAgent` helpers the ActionPanel routes through
   * so every bubble round-trips through
   * `workflow_progress.chat_messages.{agent_name}` on the backend. */
  const currentAgentName = stepToAgent(stepId);
  const chat = useChatPersistence({
    stepId,
    threadId,
    agentName: currentAgentName,
    actions,
  });

  /* ---------- Chrome flags ---------- */

  // ActionPanel hidden for agentless steps (Step 0). Derived from
  // the catalog — Step 0 has `agent: null`, agent steps don't.
  const currentCatalogStep = findStep(stepId);
  const panelHidden = currentCatalogStep?.agent == null;
  const approveHidden = false;

  // Per-step approve-override selector. Each step's body-props can
  // surface an `approveOverride: { onApprove, approving }`; when set,
  // the override fires `approveHierarchicalData` (persists relevance
  // edits). After that succeeds we chain through to the standard
  // `useApproveFlow.onApprove` which fires `resumeWorkflow` AND does
  // the optimistic writes (current → approved, next → generating,
  // selectStep forward).
  //
  // Why the chain: legacy ReviewPanel.handleApprove called
  // `approveHierarchicalData` (inner button) AND `resumeWorkflow`
  // (outer button) — two separate operations. v2's single global
  // Approve button needs to fire both. Without the chain, the
  // workflow's `review_status` is never flipped to `approved` and
  // the next agent never starts.
  const stepResultsByStepId: Record<number, { approveOverride?: { onApprove: () => Promise<void>; approving: boolean } }> = {
    2: step2Props,
    3: step3Props,
    4: step4Props,
    5: step5Props,
    6: step6Props,
    7: step7Props,
    8: step8Props,
  };
  const override = stepResultsByStepId[stepId]?.approveOverride;

  // `chainedApproving` keeps the OR of (override, approve) hooks'
  // approving flags true across the microtask gap between the two
  // awaits. Without this gate, both internal hooks' `setApproving`
  // calls can momentarily resolve false at the same time, briefly
  // re-enabling the Approve button between operations.
  const [chainedApproving, setChainedApproving] = useState<boolean>(false);
  // Story H1 — guard chained approve's microtask gap. If the user
  // unmounts between the two awaits we still let the second call
  // fire (backend consistency contract: once the save lands the
  // resume should land too) but skip the post-finally setState.
  const mountedRef = useMountedRef();

  const chainedOnApprove = useCallback(async () => {
    if (!override) return approve.onApprove();
    setChainedApproving(true);
    try {
      await override.onApprove();   // approveHierarchicalData
      await approve.onApprove();    // resumeWorkflow + optimistic writes
    } finally {
      if (mountedRef.current) setChainedApproving(false);
    }
  }, [override, approve, mountedRef]);

  const onApprove = override ? chainedOnApprove : approve.onApprove;
  const approvingNow =
    chainedApproving || (override?.approving ?? false) || approve.approving;

  /* ---------- Render ---------- */

  const renderStepBody = () => {
    if (locked) return null;
    if (stepId === UPLOAD_STEP_ID) {
      return <Step0ItemInitiation {...step0Props} actions={actions} fileId={fileId} />;
    }
    if (stepId === 1) {
      return <Step1ItemDefinition {...step1Props} />;
    }
    if (stepId === 2) {
      return <Step2AssetIdentification {...step2Props} />;
    }
    if (stepId === 3) {
      return <Step3DamageScenarios {...step3Props} />;
    }
    if (stepId === 4) {
      return <Step4ThreatIdentification {...step4Props} />;
    }
    if (stepId === 5) {
      return <Step5AttackPathFeasibility {...step5Props} />;
    }
    if (stepId === 6) {
      return <Step6RiskAssessment {...step6Props} />;
    }
    if (stepId === 7) {
      return <Step7CybersecurityGoals {...step7Props} />;
    }
    if (stepId === 8) {
      return <Step8CybersecurityConcept {...step8Props} />;
    }
    return <StepPlaceholder stepId={stepId} />;
  };

  return (
    <WorkflowV2AppShell
      state={state}
      actions={actions}
      onApprove={onApprove}
      approving={approvingNow}
      onResume={resume.onResume}
      resuming={resume.resuming}
      approveHidden={approveHidden}
      panelHidden={panelHidden}
      threadId={threadId}
      refetchStatus={refetchStatus}
      chat={chat}
    >
      <StepShell state={state} actions={actions}>
        {renderStepBody()}
      </StepShell>
    </WorkflowV2AppShell>
  );
}
